import { createSlice } from '@reduxjs/toolkit';

import {
  DEFAULT_LANGUAGE,
  MULTILANGUAGE_ENABLED,
  findSupportedLanguage,
} from '../../config/supportedLanguages';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

export const ASSET_NAME = 'landing-page';

const LANDING_PAGE_ASSET_NAMES = {
  en: ASSET_NAME,
  es: 'landing-es',
  fr: 'landing-fr',
  ja: 'landing-ja',
  zh: 'landing-zh',
};

const resolveLandingPageLanguage = languageValue => {
  if (!MULTILANGUAGE_ENABLED) {
    return DEFAULT_LANGUAGE;
  }

  return findSupportedLanguage(languageValue)?.code || DEFAULT_LANGUAGE;
};

export const getLandingPageAssetPath = languageValue => {
  const language = resolveLandingPageLanguage(languageValue);
  const assetName = LANDING_PAGE_ASSET_NAMES[language] || ASSET_NAME;
  return `content/pages/${assetName}.json`;
};

const initialState = {
  listingRefs: [],
  requestedContentLanguage: null,
};

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState,
  reducers: {
    setListings: (state, action) => {
      state.listingRefs = action.payload;
    },
    setRequestedContentLanguage: (state, action) => {
      state.requestedContentLanguage = action.payload;
    },
  },
});

export const { setListings, setRequestedContentLanguage } = landingPageSlice.actions;

export const fetchLandingPageAsset = languageValue => dispatch => {
  const language = resolveLandingPageLanguage(languageValue);
  const localizedAssetPath = getLandingPageAssetPath(language);
  const defaultAssetPath = getLandingPageAssetPath(DEFAULT_LANGUAGE);
  const fetchAsset = path => dispatch(fetchPageAssets({ landingPage: path }, true));

  dispatch(setRequestedContentLanguage(language));

  return fetchAsset(localizedAssetPath).catch(error => {
    if (localizedAssetPath === defaultAssetPath) {
      return Promise.reject(error);
    }

    return fetchAsset(defaultAssetPath);
  });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const assetsPromise = dispatch(fetchLandingPageAsset(config?.localization?.locale));

  // Fetch golden screen listings
  const listingsPromise = sdk.listings
    .query({
      pub_listingType: ['create_tour_packages', 'create_half_day_tour'],
      perPage: 100,
      include: ['images'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const goldenListings = response.data.data.filter(
        l => l.attributes.publicData?.screen_experience === 'golden_screen'
      );
      const refs = goldenListings.map(l => ({ id: l.id, type: 'listing' }));
      dispatch(setListings(refs));
      return response;
    })
    .catch(err => {
      console.error('Failed to load golden screen listings for landing page:', err);
    });

  return Promise.all([assetsPromise, listingsPromise]);
};

export default landingPageSlice.reducer;
